import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Ticket, Phone, MessageCircle, Sparkles, Inbox, Loader2, Info,
    CheckCircle2, XCircle, Building2, Search, Calendar,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { membersApi } from "@/lib/api/directory";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const STATUS_FILTERS = [
    { value: "ALL",       en: "All",        ar: "الكل"      },
    { value: "CLAIMED",   en: "Active",     ar: "نشطة"      },
    { value: "REDEEMED",  en: "Redeemed",   ar: "مستخدمة"   },
    { value: "CANCELLED", en: "Cancelled",  ar: "ملغاة"     },
];

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch (_) { return iso.slice(0, 10); }
}

function whatsappLinkFor(phone) {
    if (!phone) return null;
    return `https://wa.me/${String(phone).replace(/[^0-9]/g, "")}`;
}

// ── Claim card ──────────────────────────────────────────────────────────────
function ClaimCard({ claim, lang, onCancel, memberCode }) {
    const tr = (en, ar) => lang === "ar" ? ar : en;
    const title = lang === "ar"
        ? (claim.offer_title_ar || claim.offer_title_en)
        : (claim.offer_title_en || claim.offer_title_ar);
    const wa = whatsappLinkFor(claim.provider_whatsapp || claim.provider_phone);
    const isPercentage = claim.discount_type === "PERCENTAGE";
    const isCancelled = claim.status === "CANCELLED";
    const isRedeemed = claim.status === "REDEEMED";

    const statusBadge = isRedeemed ? (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="h-3 w-3" /> {tr("Redeemed", "مستخدمة")}
        </span>
    ) : isCancelled ? (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">
            <XCircle className="h-3 w-3" /> {tr("Cancelled", "ملغاة")}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            <Ticket className="h-3 w-3" /> {tr("Active", "نشطة")}
        </span>
    );

    return (
        <div
            className={`rounded-2xl border bg-card p-4 sm:p-5 flex flex-col sm:flex-row gap-4 ${
                isCancelled ? "border-border/40 opacity-70" : "border-border"
            }`}
            data-testid={`claim-card-${claim.id}`}
        >
            {/* Image */}
            <div className="h-32 sm:h-24 sm:w-32 rounded-xl bg-secondary shrink-0 overflow-hidden">
                {claim.offer_image_url ? (
                    <img src={claim.offer_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Ticket className="h-8 w-8 text-primary/30" />
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-base font-semibold text-foreground truncate">{title || tr("Deal", "عرض")}</h3>
                    {statusBadge}
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-2">
                    {claim.provider_name && (
                        <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {claim.provider_name}
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {tr("Claimed", "تم الحجز")} {fmtDate(claim.claimed_at, lang)}
                    </span>
                </div>

                {/* Savings line */}
                {claim.discount_amount != null && !isCancelled && (
                    <div className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                        <Sparkles className="h-3.5 w-3.5" />
                        {claim.savings_is_estimated && isPercentage
                            ? tr(
                                `Saving ~${Math.round(claim.discount_amount)} EGP (estimated)`,
                                `توفير ~${Math.round(claim.discount_amount)} ج.م (تقديري)`
                              )
                            : tr(
                                `Saved ${Math.round(claim.discount_amount)} EGP`,
                                `وفرت ${Math.round(claim.discount_amount)} ج.م`
                              )}
                    </div>
                )}

                {/* Action row */}
                {!isCancelled && (
                    <div className="flex flex-wrap items-center gap-2">
                        {claim.provider_phone && (
                            <a
                                href={`tel:${claim.provider_phone}`}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90"
                                data-testid={`claim-call-${claim.id}`}
                            >
                                <Phone className="h-3.5 w-3.5" />
                                {claim.provider_phone}
                            </a>
                        )}
                        {wa && (
                            <a
                                href={wa}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#25D366]/10 text-[#128C7E] border border-[#25D366]/30 text-xs font-semibold hover:bg-[#25D366]/20"
                            >
                                <MessageCircle className="h-3.5 w-3.5" />
                                {tr("WhatsApp", "واتساب")}
                            </a>
                        )}
                        <Link
                            to={`/deal/${claim.offer_id}`}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80"
                        >
                            {tr("View deal", "عرض التفاصيل")}
                        </Link>
                        {!isRedeemed && (
                            <button
                                onClick={() => onCancel(claim)}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-destructive/70 text-xs font-medium hover:text-destructive hover:bg-destructive/5"
                                data-testid={`claim-cancel-${claim.id}`}
                            >
                                {tr("Remove", "حذف")}
                            </button>
                        )}
                    </div>
                )}

                {/* Reminder strip */}
                {!isCancelled && claim.provider_phone && memberCode && (
                    <p className="mt-3 text-[11px] text-muted-foreground">
                        {tr(
                            `Mention your code ${memberCode} when you call.`,
                            `اذكر كود ${memberCode} عند الاتصال.`
                        )}
                    </p>
                )}
            </div>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function MyClaimsPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [data, setData] = useState(null);
    const [me, setMe] = useState(null);
    const [filter, setFilter] = useState("ALL");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [claims, member] = await Promise.all([membersApi.listClaims(), membersApi.me()]);
            setData(claims);
            setMe(member);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleCancel = async (claim) => {
        if (!window.confirm(tr("Remove this claim?", "هل تريد إزالة هذا الحجز؟"))) return;
        try {
            await membersApi.cancelClaim(claim.id);
            toast.success(tr("Claim removed", "تم إزالة الحجز"));
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const filteredItems = useMemo(() => {
        const items = data?.items || [];
        if (filter === "ALL") return items;
        return items.filter((c) => (c.status || "CLAIMED") === filter);
    }, [data, filter]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const totalSavings = data?.total_savings ?? 0;
    const claimedCount = data?.claimed_count ?? 0;
    const activeCount = data?.active_count ?? 0;
    const redeemedCount = data?.redeemed_count ?? 0;
    const hasEstimates = data?.savings_includes_estimates;

    return (
        <div className="max-w-3xl space-y-6 fade-up" data-testid="page-my-claims">
            {/* Header */}
            <header>
                <p className="text-sm font-medium text-primary">{tr("Your savings", "مدخراتك")}</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight">{tr("My Deals", "عروضي")}</h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                    {tr(
                        "Every deal you've claimed and how much you've saved.",
                        "كل العروض التي حجزتها ومقدار ما وفّرته."
                    )}
                </p>
            </header>

            {/* Savings hero */}
            <div className="rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-emerald-50/30 p-6">
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <Sparkles className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                            {tr("Total savings", "إجمالي التوفير")}
                        </p>
                        <p className="mt-1 text-3xl font-bold tracking-tight text-emerald-900">
                            {Math.round(totalSavings).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}{" "}
                            <span className="text-base font-semibold text-emerald-700">
                                {tr("EGP", "ج.م")}
                            </span>
                        </p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-emerald-800/80">
                            <span><strong className="font-semibold">{claimedCount}</strong> {tr("deals claimed", "عرض محجوز")}</span>
                            <span><strong className="font-semibold">{activeCount}</strong> {tr("active", "نشط")}</span>
                            <span><strong className="font-semibold">{redeemedCount}</strong> {tr("used", "مستخدم")}</span>
                        </div>
                        {hasEstimates && (
                            <p className="mt-2 text-[11px] text-emerald-800/70 inline-flex items-center gap-1">
                                <Info className="h-3 w-3" />
                                {tr(
                                    "Some savings are estimates — replaced by the real amount when the clinic logs your visit.",
                                    "بعض القيم تقديرية — تُستبدل بالقيمة الفعلية بعد تسجيل العيادة لزيارتك."
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                {STATUS_FILTERS.map((f) => (
                    <button
                        key={f.value}
                        onClick={() => setFilter(f.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                            filter === f.value
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`claim-filter-${f.value}`}
                    >
                        {lang === "ar" ? f.ar : f.en}
                    </button>
                ))}
            </div>

            {/* List */}
            {filteredItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-12 flex flex-col items-center gap-3 text-center">
                    <Inbox className="h-10 w-10 text-muted-foreground" />
                    <div>
                        <p className="text-sm font-medium text-foreground">
                            {filter === "ALL"
                                ? tr("No deals claimed yet", "لم تحجز أي عرض بعد")
                                : tr("Nothing here", "لا يوجد شيء هنا")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {tr(
                                "Browse deals and claim one to start saving.",
                                "تصفح العروض واحجز واحداً لبدء التوفير."
                            )}
                        </p>
                    </div>
                    <Link
                        to="/member/home"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
                    >
                        <Search className="h-3.5 w-3.5" />
                        {tr("Browse deals", "تصفح العروض")}
                    </Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredItems.map((c) => (
                        <ClaimCard
                            key={c.id}
                            claim={c}
                            lang={lang}
                            onCancel={handleCancel}
                            memberCode={me?.member_code}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
